import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { SCENARIO_COLORS } from './analysisMetrics';
import { formatYAxis } from './analysisCompareUtils';
import AssetsByTypeChart from './AssetsByTypeChart';
import CompareCashflowTooltip from './CompareCashflowTooltip';

const STACK_COLORS = [
  '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#3b82f6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16',
];

const AnalysisCharts = ({
  projection = [],
  bundle = null,
  assetNames = [],
  revenueNames = [],
  activeChartTab = 'netWorth',
  formatCurrency,
  compareMergedData = null,
  labelA = 'Scénario A',
  labelB = 'Scénario B',
  yearIndex = 0,
}) => {
  const isCompare = compareMergedData?.length > 0;
  const chartData = isCompare ? compareMergedData : projection;

  const colorMap = useMemo(() => {
    const map = {};
    assetNames.forEach((name, i) => { map[name] = STACK_COLORS[i % STACK_COLORS.length]; });
    return map;
  }, [assetNames]);

  const revColorMap = useMemo(() => {
    const map = {};
    revenueNames.forEach((name, i) => { map[name] = STACK_COLORS[(i + 5) % STACK_COLORS.length]; });
    return map;
  }, [revenueNames]);

  const NetWorthTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-lg text-xs">
        <p className="font-semibold mb-1">{row?.year}</p>
        {isCompare ? (
          <>
            <p><span className="text-muted-foreground">{labelA}:</span> {formatCurrency(row?.netWorthA)}</p>
            <p><span className="text-muted-foreground">{labelB}:</span> {formatCurrency(row?.netWorthB)}</p>
            <p className="font-semibold mt-1" style={{ color: row?.gapNetWorth >= 0 ? 'var(--color-success)' : 'var(--color-destructive)' }}>
              Écart: {formatCurrency(row?.gapNetWorth)}
            </p>
          </>
        ) : (
          <>
            <p>Actifs: {formatCurrency(row?.totalAssets)}</p>
            {(row?.assuranceParticipativeTotal || 0) > 0 && (
              <p>Assurance (rachat): {formatCurrency(row.assuranceParticipativeTotal)}</p>
            )}
            <p>Dettes: {formatCurrency(row?.totalDebts)}</p>
            <p className="font-semibold">VN: {formatCurrency(row?.netWorth)}</p>
            <p>Impôt: {formatCurrency(row?.annualTax)}</p>
            <p>Impact cashflow: {formatCurrency(row?.cashflowImpactTotal ?? ((row?.cashflowImpactPersonnel ?? 0) + (row?.cashflowImpactCorporation ?? 0)))}</p>
            <p>Cashflow entité: {formatCurrency(row?.annualCashflowAfterTax)}</p>
          </>
        )}
      </div>
    );
  };

  if (!chartData?.length) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">Ajoutez des données au bilan pour voir les graphiques.</div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4 md:p-5" data-testid="analysis-charts">
      {activeChartTab === 'netWorth' && (
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            {isCompare ? (
              <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="year" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
                <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} width={56} />
                <Tooltip content={<NetWorthTooltip />} />
                <Legend wrapperStyle={{ fontSize: '0.7rem' }} />
                <Line type="monotone" dataKey="netWorthA" name={labelA} stroke={SCENARIO_COLORS.A} strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="netWorthB" name={labelB} stroke={SCENARIO_COLORS.B} strokeWidth={2.5} dot={false} strokeDasharray="6 3" />
              </LineChart>
            ) : (
              <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="year" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
                <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} width={56} />
                <Tooltip content={<NetWorthTooltip />} />
                <Line type="monotone" dataKey="netWorth" name="Valeur nette" stroke={SCENARIO_COLORS.A} strokeWidth={2.5} dot={false} />
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {activeChartTab === 'debtVsAssets' && !isCompare && (
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
              <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} width={56} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Legend wrapperStyle={{ fontSize: '0.7rem' }} />
              <Area type="monotone" dataKey="totalAssets" name="Actifs" stroke="#22c55e" fill="#22c55e22" strokeWidth={2} />
              <Area type="monotone" dataKey="totalDebts" name="Dettes" stroke="#ef4444" fill="#ef444422" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {activeChartTab === 'cashflow' && (
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            {isCompare ? (
              <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="year" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
                <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} width={56} />
                <Tooltip
                  content={(
                    <CompareCashflowTooltip
                      labelA={labelA}
                      labelB={labelB}
                      formatCurrency={formatCurrency}
                    />
                  )}
                />
                <Legend wrapperStyle={{ fontSize: '0.7rem' }} />
                <Line type="monotone" dataKey="cashflowA" name={labelA} stroke={SCENARIO_COLORS.A} strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="cashflowB" name={labelB} stroke={SCENARIO_COLORS.B} strokeWidth={2.5} dot={false} strokeDasharray="6 3" />
              </LineChart>
            ) : (
              <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="year" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
                <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} width={56} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend wrapperStyle={{ fontSize: '0.7rem' }} />
                <Line type="monotone" dataKey="cashflowImpactTotal" name="Impact cashflow (actifs)" stroke="#22c55e" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="annualCashflowAfterTax" name="Cashflow net (entité)" stroke="#3b82f6" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
                <Line type="monotone" dataKey="annualTax" name="Impôt" stroke="#ef4444" strokeWidth={1.5} dot={false} />
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {activeChartTab === 'assetsStack' && !isCompare && assetNames.length > 0 && (
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
              <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} width={56} />
              <Tooltip formatter={(v, name) => [formatCurrency(v), name]} />
              <Legend wrapperStyle={{ fontSize: '0.65rem' }} />
              {assetNames.map((name) => (
                <Area key={name} type="monotone" dataKey={name} stackId="a" stroke={colorMap[name]} fill={colorMap[name] + '55'} strokeWidth={1} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {activeChartTab === 'revenuesStack' && !isCompare && revenueNames.length > 0 && (
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
              <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} width={56} />
              <Tooltip formatter={(v, name) => [formatCurrency(v), String(name).replace('rev::', '')]} />
              <Legend wrapperStyle={{ fontSize: '0.65rem' }} formatter={(v) => String(v).replace('rev::', '')} />
              {revenueNames.map((name) => (
                <Area key={name} type="monotone" dataKey={`rev::${name}`} stackId="r" stroke={revColorMap[name]} fill={revColorMap[name] + '55'} strokeWidth={1} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {activeChartTab === 'assetsStack' && isCompare && (
        <div />
      )}

      {activeChartTab === 'byAssetType' && !isCompare && (
        <AssetsByTypeChart
          projection={projection}
          bundle={bundle}
          yearIndex={yearIndex}
          formatCurrency={formatCurrency}
        />
      )}

      {activeChartTab === 'byAssetType' && isCompare && (
        <p className="text-sm text-muted-foreground text-center py-8">
          La vue par type d&apos;actif est disponible hors mode comparaison A/B (même scénario pour A et B).
        </p>
      )}
    </div>
  );
};

export default AnalysisCharts;
